import {
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Icon,
  Input,
  useToast,
} from "@chakra-ui/react";
import { RegisterOptions, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FaSave } from "react-icons/fa";
import { EditOrganizationRequest } from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { useSaveOrganization } from "../useOrganizationEditor";

interface Props {
  currentValue: string;
  fieldName: string;
  label: string;
  helperText: string;
  toastMessage: string;
  buildPatch: (value: string | null) => Partial<EditOrganizationRequest>;
  validation?: RegisterOptions;
}

interface FormValues {
  field: string;
}

export const ChangeOrgTextField = ({
  currentValue,
  fieldName,
  label,
  helperText,
  toastMessage,
  buildPatch,
  validation,
}: Props) => {
  const toast = useToast();
  const { saveOrganization, isSaving } = useSaveOrganization();
  const { t } = useTranslation();
  const displayer = useApiError();

  const {
    formState: { errors },
    register,
    handleSubmit,
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      field: currentValue,
    },
  });

  const value = watch("field");

  const onSubmit = async (values: FormValues) => {
    try {
      await saveOrganization(buildPatch(values.field || null));
      toast({ title: toastMessage, status: "success" });
    } catch (e) {
      displayer.trigger(e);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormControl mt="15px" isInvalid={errors.field !== undefined}>
        <FormLabel htmlFor={fieldName}>{label}</FormLabel>
        <Input id={fieldName} type="text" {...register("field", validation)} />
        <FormErrorMessage>{errors.field && errors.field.message}</FormErrorMessage>
        <FormHelperText>{helperText}</FormHelperText>
      </FormControl>
      {currentValue.trim() !== value.trim() && (
        <Button
          mt="10px"
          leftIcon={<Icon as={FaSave} w="15px" h="15px" mt="-2px" />}
          variant={"solid"}
          colorScheme="teal"
          size={"sm"}
          isLoading={isSaving}
          type="submit"
        >
          {t("Save change")}
        </Button>
      )}
    </form>
  );
};
